import React, { useMemo } from 'react';

import './UsableFields.scss';
import { Badge } from 'react-bootstrap';
import { InternalApiUsableField, UsableFieldType } from './usableFieldTypes';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/store';
import { createColumnHelper } from '@tanstack/react-table';
import { SalvTable, Size } from '@salvapp/ui-components';
import Has from '../../auth/Has';
import {
  CAN_ADD_CUSTOM_LIST_MONITORING_FIELD,
  CAN_ADD_CUSTOM_LIST_SCREENING_FIELD,
  CAN_READ_CUSTOM_LIST_MONITORING_FIELD,
  CAN_READ_CUSTOM_LIST_SCREENING_FIELD,
} from '../../auth/permissions';
import UsableFieldsModal from './UsableFieldsModal';
import { clsx } from 'clsx';

type Props = {
  type: UsableFieldType;
  canEdit: boolean;
  className?: string;
};

export const UsableFields = ({ type, canEdit, className = '' }: Props) => {
  const usableFields = useSelector((app: AppState) =>
    type === UsableFieldType.SCREENING ? app.customList.usableScreeningFields : app.customList.usableMonitoringFields
  );

  const schemaFields = useSelector((state: AppState) => state.customList.schemaFields);
  const additionalDataSchemaFields = schemaFields.filter((field) => !field.parentId);

  const columnHelper = createColumnHelper<InternalApiUsableField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.accessor('code', {
        header: 'Code',
      }),
      columnHelper.accessor('path', {
        header: 'Field',
        cell: ({ getValue }) => <Badge bg='primary'>{getValue()}</Badge>,
      }),
      columnHelper.accessor('additionalSchemaFields', {
        header: 'Additional data',
        cell: ({ getValue }) =>
          additionalDataSchemaFields.length && (
            <Badge bg='primary'>
              {(getValue() as string[] | undefined)
                ?.map((schemaFieldId) => {
                  const schemaField = additionalDataSchemaFields.find(
                    (schemaField) => schemaField.id === schemaFieldId
                  );

                  return schemaField ? schemaField.fieldName : 'Error: Associated Schema field does not exist anymore';
                })
                .join(' ')}
            </Badge>
          ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
      }),
      columnHelper.accessor('rule', {
        header: 'Type rule',
      }),
    ],
    [type, additionalDataSchemaFields]
  );
  const title = type === UsableFieldType.SCREENING ? 'Screening' : 'Monitoring';

  const classes = clsx(className);
  return (
    <Has
      allPermissions={
        type === UsableFieldType.SCREENING
          ? CAN_READ_CUSTOM_LIST_SCREENING_FIELD
          : CAN_READ_CUSTOM_LIST_MONITORING_FIELD
      }
    >
      <SalvTable
        title={title}
        columns={columns}
        columnVisibility={{
          additionalSchemaFields: type === UsableFieldType.MONITORING,
          type: type === UsableFieldType.SCREENING,
          rule: type === UsableFieldType.SCREENING,
        }}
        data={usableFields}
        size={Size.SMALL}
        striped
        emptyMessage={'Usable fields are not yet defined'}
        className={classes}
        actions={
          canEdit && (
            <Has
              allPermissions={
                type === UsableFieldType.SCREENING
                  ? CAN_ADD_CUSTOM_LIST_SCREENING_FIELD
                  : CAN_ADD_CUSTOM_LIST_MONITORING_FIELD
              }
            >
              <UsableFieldsModal type={type} usableFields={usableFields} />
            </Has>
          )
        }
      />
    </Has>
  );
};

export default UsableFields;
